import type { AssetImpact, AssetImpactAction, PublicationValidation } from '../types';

/**
 * The fields a blocked drawing carries in both shapes the publish drawer holds:
 * a plan candidate and a `plant_publication_blocked` blocker from a failed POST.
 * Typed structurally so one reason reader serves both.
 */
type BlockedDrawingFields = {
  block_code?: string | null;
  block_message?: string | null;
  validation?: PublicationValidation | null;
};

/** Drawings blocked for the same set of reasons, so the drawer states each set once. */
export type BlockedReasonGroup<T> = {
  reasons: string[];
  drawings: T[];
};

/**
 * Why this drawing cannot publish, in the resolver's own words — one sentence per
 * unresolved blocking conflict, which is the per-field detail the generic block
 * message flattens away. Falls back to that message when the response carries no
 * conflicts, and never returns an empty list.
 */
export function blockReasons(drawing: BlockedDrawingFields): string[] {
  const conflicts = (drawing.validation?.conflicts ?? [])
    .filter((conflict) => conflict.blocking && !conflict.resolved)
    .map((conflict) => conflict.message);
  if (conflicts.length > 0) return conflicts;
  if (drawing.block_message) return [drawing.block_message];
  if (drawing.block_code) return [drawing.block_code];
  return ['Cannot publish'];
}

/**
 * Collapse blocked drawings into one group per distinct reason set, largest group
 * first: a plant blocked on the same two missing fields across thirty drawings
 * reads as one statement rather than thirty repetitions.
 *
 * Reasons keep the resolver's field order inside a group, so a set is only ever
 * equal to another set that arrived in the same order.
 */
export function groupBlockedDrawings<T extends BlockedDrawingFields>(
  drawings: T[],
): BlockedReasonGroup<T>[] {
  const groupsByKey = new Map<string, BlockedReasonGroup<T>>();
  for (const drawing of drawings) {
    const reasons = blockReasons(drawing);
    const key = reasons.join('\n');
    const group = groupsByKey.get(key);
    if (group) {
      group.drawings.push(drawing);
    } else {
      groupsByKey.set(key, { reasons, drawings: [drawing] });
    }
  }
  return [...groupsByKey.entries()]
    .sort(([leftKey, left], [rightKey, right]) =>
      right.drawings.length - left.drawings.length || leftKey.localeCompare(rightKey),
    )
    .map(([, group]) => group);
}

/**
 * Why a register write that carries this action cannot go through, in one sentence.
 * Null for an action that lets the publish proceed.
 */
export function assetImpactBlockReason(action: AssetImpactAction): string | null {
  if (action === 'skip_tag_collision') {
    return 'Renamed to a tag that already exists on another asset.';
  }
  if (action === 'skip_shared_asset') {
    return "Rename skipped — other elements on this drawing still use this asset's tag.";
  }
  if (action === 'skip_ambiguous_candidates') {
    return 'Conflicting candidates on this drawing — needs review.';
  }
  return null;
}

/** The register outcomes that stop a publish rather than merely skipping one tag. */
export const BLOCKING_ASSET_IMPACT_ACTIONS: ReadonlySet<AssetImpactAction> = new Set([
  'skip_tag_collision',
  'skip_shared_asset',
  'skip_ambiguous_candidates',
]);

/**
 * The register rows that block the whole publish. The action fallback covers a
 * backend that does not yet send `blocking` on each impact row.
 */
export function cohortBlockingImpacts(impacts: AssetImpact[]): AssetImpact[] {
  return impacts.filter(
    (impact) => impact.blocking ?? BLOCKING_ASSET_IMPACT_ACTIONS.has(impact.action),
  );
}
